import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import TextInput from '../../components/FormElements/TextInput';
import SelectInput from '../../components/FormElements/SelectInput';
import ArrowLeftIcon from '../../components/icons/ArrowLeftIcon';
import { useAuth } from '../../contexts/AuthContext'; // Re-added useAuth import

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ContractFormAdminScreen = () => {
    const { contractId } = useParams(); // For potential edit mode
    const isEditMode = Boolean(contractId);
    const { token } = useAuth(); // Re-enabled useAuth hook
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        id: '', // Will be set if editing, or generated on creation
        fechaContrato: '',
        clienteSeleccionado: '',
        destinoPrincipal: '',
        destinosDetallados: '',
        fechaInicioViaje: '',
        fechaFinViaje: '',
        duracionDias: '',
        duracionNoches: '',
        numeroPasajeros: '',
        serviciosIncluidos: '',
        serviciosNoIncluidos: '',
        precioTotal: '',
        formaPago: '',
        estadoContrato: '',
        politicasCancelacion: '',
        responsabilidades: '',
        jurisdiccion: 'Viña del Mar, Región de Valparaíso, Chile', // Default value
        consideracionesAdicionales: '',
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false); // For edit mode loading
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
    const alertRef = useRef(null);

    // Mock options for select inputs
    const clientOptions = [
        { value: "", label: "Buscar o seleccionar un cliente existente" },
        { value: "cliente1", label: "Cliente Ejemplo 1 (ID: XXX)" },
        { value: "cliente2", label: "Cliente Ejemplo 2 (ID: YYY)" },
    ];

    const contractStatusOptions = [
        { value: "", label: "Seleccione un estado" },
        { value: "activo", label: "Activo" },
        { value: "pendiente", label: "Pendiente de Pago" },
        { value: "finalizado", label: "Finalizado" },
        { value: "cancelado", label: "Cancelado" },
        { value: "borrador", label: "Borrador" },
    ];

    useEffect(() => {
        // Set current date for 'fechaContrato' on component mount if not in edit mode
        if (!isEditMode) {
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];
            setFormData(prev => ({ ...prev, fechaContrato: formattedDate }));
        }

        // In a real application, if isEditMode, you would fetch contract data here
        if (isEditMode && contractId && token) {
            setIsLoadingData(true);
            // Simulate API call to fetch contract details
            setTimeout(() => {
                const mockContractDetails = {
                    id: contractId,
                    fechaContrato: '2024-06-20',
                    clienteSeleccionado: 'cliente1',
                    destinoPrincipal: 'París, Francia',
                    destinosDetallados: 'París, Roma, Venecia',
                    fechaInicioViaje: '2024-09-01',
                    fechaFinViaje: '2024-09-15',
                    duracionDias: '15',
                    duracionNoches: '14',
                    numeroPasajeros: '2',
                    serviciosIncluidos: 'Alojamiento en hoteles 4 estrellas, vuelos ida y vuelta, tours guiados, desayuno diario.',
                    serviciosNoIncluidos: 'Cenas, seguro de viaje, gastos personales.',
                    precioTotal: 'USD 5,000 (Cinco mil dólares americanos)',
                    formaPago: 'Transferencia bancaria, 50% al reservar, 50% 30 días antes del viaje.',
                    estadoContrato: 'borrador',
                    politicasCancelacion: 'Cancelación con 60 días de anticipación: reembolso completo. Menos de 60 días: penalización del 25%.',
                    responsabilidades: 'Agente: organizar y reservar. Cliente: proporcionar información veraz y cumplir pagos.',
                    jurisdiccion: 'Viña del Mar, Región de Valparaíso, Chile',
                    consideracionesAdicionales: 'Itinerario adjunto. Firmar cada página. Protección de datos según GDPR.',
                };
                setFormData(mockContractDetails);
                setIsLoadingData(false);
            }, 700);
        }
    }, [isEditMode, contractId, token]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitContract = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage({ type: '', text: '' });

        // Basic validation
        if (!formData.clienteSeleccionado || !formData.destinoPrincipal || !formData.fechaInicioViaje || !formData.fechaFinViaje) {
            setSubmitMessage({ type: 'danger', text: 'Por favor, complete los campos obligatorios (Cliente, Destino Principal, Fechas de Viaje).' });
            setIsSubmitting(false);
            return;
        }

        const payload = { ...formData };
        // In a real app, you'd send this payload to your API
        console.log("Submitting contract:", payload);

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // const url = isEditMode ? `${API_BASE_URL}/contracts/${contractId}` : `${API_BASE_URL}/contracts`;
            // const method = isEditMode ? 'PUT' : 'POST';
            // const response = await fetch(url, {
            //     method: method,
            //     headers: {
            //         'Content-Type': 'application/json',
            //         ...(token && { 'Authorization': `Bearer ${token}` }),
            //     },
            //     body: JSON.stringify(payload),
            // });
            // if (!response.ok) {
            //     const errorData = await response.json().catch(() => ({ message: `Error del servidor: ${response.status}` }));
            //     throw new Error(errorData.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el contrato: ${response.statusText}`);
            // }
            // const responseData = await response.json();

            setSubmitMessage({ type: 'success', text: `¡Contrato ${isEditMode ? 'actualizado' : 'guardado'} con éxito!` });
            setTimeout(() => {
                navigate('/contracts'); // Redirect to contract list
            }, 1500);

        } catch (error) {
            console.error("Error submitting contract:", error);
            setSubmitMessage({ type: 'danger', text: error.message || `No se pudo ${isEditMode ? 'actualizar' : 'guardar'} el contrato. Intente nuevamente.` });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (submitMessage.text && alertRef.current) {
            alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [submitMessage.text]);

    return (
        <> {/* Use a React Fragment as the root element, as DashboardLayout already provides the main content area */}
            <FormSpinnerOverlay isLoading={isLoadingData || isSubmitting} />
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <div className="d-flex align-items-center">
                    <Link to="/contracts" className="btn btn-back-circle me-3" title="Volver a Contratos">
                        <ArrowLeftIcon />
                    </Link>
                    <h1 className="h2 mb-0">{isEditMode ? `Editar Contrato: ${contractId}` : 'Crear Nuevo Contrato'}</h1>
                </div>
            </div>

            <div className="row text-start mb-4">
                <p className="mb-4 text-muted">Complete el formulario para {isEditMode ? 'editar' : 'crear'} un contrato.</p>
                <div className="col-12">
                    <div className="card border-0 text-start"> {/* Added text-start for consistency */}
                        <div className="card-body px-md-4 px-3" style={{ opacity: (isLoadingData || isSubmitting) ? 0.7 : 1 }}> {/* Re-added original padding classes */}
                            <div ref={alertRef} style={{ minHeight: '60px' }}>
                                {submitMessage.text && <AlertMessage type={submitMessage.type} message={submitMessage.text} />}
                            </div>
                            <form onSubmit={handleSubmitContract}>
                                <TextInput label="Fecha del Contrato (Automática)" id="fechaContrato" name="fechaContrato" type="date" value={formData.fechaContrato} onChange={handleInputChange} readOnly={true} />

                                <h5 className="mt-4 mb-3">Información del Cliente</h5>
                                <SelectInput label="Seleccionar Cliente" id="clienteSeleccionado" name="clienteSeleccionado" value={formData.clienteSeleccionado} onChange={handleInputChange} options={clientOptions} required={true} />

                                <h5 className="mt-4 mb-3">Objeto del Contrato</h5>
                                <TextInput label="Destino Principal del Viaje" id="destinoPrincipal" name="destinoPrincipal" value={formData.destinoPrincipal} onChange={handleInputChange} placeholder="Destino Principal del Viaje" required={true} />

                                <h5 className="mt-4 mb-3">Detalles del Viaje</h5>
                                <TextInput label="Destino(s) Detallados" id="destinosDetallados" name="destinosDetallados" type="textarea" rows="2" value={formData.destinosDetallados} onChange={handleInputChange} placeholder="Ciudades, Países, etc., incluidos en el itinerario" />
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <TextInput label="Fecha de Inicio del Viaje" id="fechaInicioViaje" name="fechaInicioViaje" type="date" value={formData.fechaInicioViaje} onChange={handleInputChange} required={true} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <TextInput label="Fecha de Fin del Viaje" id="fechaFinViaje" name="fechaFinViaje" type="date" value={formData.fechaFinViaje} onChange={handleInputChange} required={true} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <TextInput label="Duración (Días)" id="duracionDias" name="duracionDias" type="number" value={formData.duracionDias} onChange={handleInputChange} placeholder="Cantidad de Días" />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <TextInput label="Duración (Noches)" id="duracionNoches" name="duracionNoches" type="number" value={formData.duracionNoches} onChange={handleInputChange} placeholder="Cantidad de Noches" />
                                    </div>
                                </div>
                                <TextInput label="Número de Pasajeros" id="numeroPasajeros" name="numeroPasajeros" type="number" value={formData.numeroPasajeros} onChange={handleInputChange} placeholder="Número de personas que viajan" />

                                <h5 className="mt-4 mb-3">Servicios</h5>
                                <TextInput label="Servicios Incluidos" id="serviciosIncluidos" name="serviciosIncluidos" type="textarea" rows="5" value={formData.serviciosIncluidos} onChange={handleInputChange} placeholder="Detallar alojamiento, transporte aéreo/terrestre, excursiones, alimentación, guía, seguro, asistencia, etc." helpText="Referenciar al itinerario final aprobado por el Cliente." />
                                <TextInput label="Servicios No Incluidos" id="serviciosNoIncluidos" name="serviciosNoIncluidos" type="textarea" rows="3" value={formData.serviciosNoIncluidos} onChange={handleInputChange} placeholder="Gastos personales, comidas no especificadas, visados, propinas, etc." />

                                <h5 className="mt-4 mb-3">Condiciones de Pago</h5>
                                <TextInput label="Precio Total del Viaje" id="precioTotal" name="precioTotal" value={formData.precioTotal} onChange={handleInputChange} placeholder="USD/CLP [Monto total en número y letras]" />
                                <TextInput label="Forma de Pago" id="formaPago" name="formaPago" type="textarea" rows="2" value={formData.formaPago} onChange={handleInputChange} placeholder="Detallar si es transferencia bancaria, tarjeta de crédito, cuotas, etc." />
                                <SelectInput label="Estado del Contrato" id="estadoContrato" name="estadoContrato" value={formData.estadoContrato} onChange={handleInputChange} options={contractStatusOptions} />
                                <TextInput label="Políticas de Cancelación y Modificación" id="politicasCancelacion" name="politicasCancelacion" type="textarea" rows="4" value={formData.politicasCancelacion} onChange={handleInputChange} placeholder="Detallar condiciones de cancelación y modificación por parte del cliente y del agente." />
                                <TextInput label="Responsabilidades" id="responsabilidades" name="responsabilidades" type="textarea" rows="4" value={formData.responsabilidades} onChange={handleInputChange} placeholder="Detallar responsabilidades del Agente de Viajes y del Cliente." />
                                <TextInput label="Jurisdicción" id="jurisdiccion" name="jurisdiccion" value={formData.jurisdiccion} onChange={handleInputChange} placeholder="Ciudad y Región para la jurisdicción" />
                                <TextInput label="Consideraciones Adicionales / Notas Internas" id="consideracionesAdicionales" name="consideracionesAdicionales" type="textarea" rows="3" value={formData.consideracionesAdicionales} onChange={handleInputChange} placeholder="Notas sobre adjuntar itinerario, firma de páginas, protección de datos, menores, etc." />

                                <div className="mt-4">
                                    <button type="submit" className="btn btn-success me-2" disabled={isSubmitting || isLoadingData}>
                                        {isSubmitting ? 'Guardando...' : (isEditMode ? 'Actualizar Contrato' : 'Guardar Contrato')}
                                    </button>
                                    <Link to="/contracts" className="btn btn-outline-secondary">Cancelar</Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContractFormAdminScreen;